import { useSelector } from 'react-redux';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { ThunkAction } from 'redux-thunk';
import { AnyAction, Store } from 'redux';

import { Product } from '../helpers';
import State from '../../app/store/state';
import {
    loadProduct,
    createProductUpdateRequest,
    loadProductImage,
    loadProductUnits,
    loadProductQuantityOptions,
    loadProductLimitationGroups,
    hasPendingProductImageUpdate,
} from '../../apiClient';
import { errorAction, successAction } from '../../utils/notifications';
import { ActionCreators as AuthActionCreators } from '../../login/auth';
import { wfpFormat } from '../../utils/utils';
import { useAppDispatch, dispatch as appDispatch, RootAppState } from '../../app/store/store';

type SelectOptionString = { label: string; value: string };
type ProductImage = { url: string; version: number | string };

export function useProductDetails(params: { productId: string }) {
    const dispatch = useAppDispatch();
    const productId = params.productId;
    const [product, setProduct] = useState<Product | null>(null);
    const [originalProduct, setOriginalProduct] = useState<Product | null>(null);
    const [productImage, setProductImage] = useState<ProductImage | null>(null);
    const [editing, setEditing] = useState(false);
    const [hasPendingImageUpdate, setHasPendingImageUpdate] = useState(false);
    const [stagedImageKey, setStagedImageKey] = useState<string | null>(null);
    const [stagedImagePreviewUrl, setStagedImagePreviewUrl] = useState<string | null>(null);
    const [units, setUnits] = useState<SelectOptionString[]>([]);
    const [quantityOptions, setQuantityOptions] = useState<SelectOptionString[]>([]);
    const [limitationGroups, setLimitationGroups] = useState<SelectOptionString[]>([]);
    const categories = useSelector((state: State) =>
        state.appConfig.entitlementsConfig.categories.map((category) => ({
            value: category,
            label: category,
        }))
    );
    const tz = useSelector((state: State) => state.appConfig.timeZone);

    useEffect(() => {
        loadProduct(productId).then((loadedProduct) => {
            setProduct(loadedProduct);
            setOriginalProduct(loadedProduct);
        });
        // Check if there is a pending product details update request; if so, we will hide the image
        hasPendingProductImageUpdate(String(productId))
            .then((pending) => {
                setHasPendingImageUpdate(pending);
                if (pending) {
                    setProductImage(null);
                    return;
                }
                return loadProductImage(productId)
                    .then(setProductImage)
                    .catch(() => setProductImage(null));
            })
            .catch(() => {
                loadProductImage(productId)
                    .then(setProductImage)
                    .catch(() => setProductImage(null));
            });

        // Load dropdown data for edit mode with proper transformation
        Promise.all([loadProductUnits(), loadProductQuantityOptions(), loadProductLimitationGroups()])
            .then(
                ([unitsData, quantityOptionsData, limitationGroupsData]: [
                    Record<string, string>,
                    Record<string, string>,
                    Array<{ id: number; name: string }>,
                ]) => {
                    setUnits(
                        Object.entries(unitsData).map(([key, value]) => ({
                            label: `${value} - ${key}`,
                            value: `${value} - ${key}`,
                        }))
                    );

                    setQuantityOptions(
                        Object.values(quantityOptionsData)
                            .filter((val: string) => String(val).toLowerCase() !== 'fixed')
                            .map((val: string) => ({
                                value: wfpFormat(val),
                                label: wfpFormat(val),
                            }))
                    );

                    setLimitationGroups(
                        limitationGroupsData.map((group) => ({
                            value: String(group.id),
                            label: group.name,
                        }))
                    );
                }
            )
            .catch(() => {
                setUnits([]);
                setQuantityOptions([]);
                setLimitationGroups([]);
                dispatch(errorAction('Failed to load product options'));
            });
    }, []);

    const getChangedFields = useCallback(() => {
        if (!originalProduct || !product) return {};

        const changes: Partial<Product> = { id: product.id };
        const forbiddenFields = ['authorizedAt', 'authorizedByManagerId', 'createdAt', 'createdByManagerId'];

        const keys = Object.keys(product) as Array<keyof Product>;
        const writableChanges = changes as Record<keyof Product, Product[keyof Product]>;
        keys.forEach((key) => {
            if (forbiddenFields.includes(String(key))) return;
            const originalValue = originalProduct![key] as unknown;
            const currentValue = product[key] as unknown;
            if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
                const a = [...originalValue].sort();
                const b = [...currentValue].sort();
                if (JSON.stringify(a) !== JSON.stringify(b)) {
                    writableChanges[key] = product[key] as Product[keyof Product];
                }
            } else if (originalValue !== currentValue) {
                writableChanges[key] = product[key] as Product[keyof Product];
            }
        });
        return changes;
    }, [originalProduct, product]);

    const handleSubmit = (evt?: { preventDefault?: () => void }) => {
        evt?.preventDefault?.();
        // Required fields validation (same as Add Product)
        const missingFields: string[] = [];
        if (!product?.name) missingFields.push('Name');
        if (!product?.category) missingFields.push('Category');
        if (!(product?.maxAllowedQuantity === 1) && !product?.quantityOption) missingFields.push('Quantity Option');
        if (!product?.unit) missingFields.push('Unit');
        if (!product?.groups || product.groups.length === 0) missingFields.push('Groups');

        if (missingFields.length > 0) {
            dispatch(errorAction(`Please fill required fields: ${missingFields.join(', ')}`));
            return;
        }

        const changedFields = getChangedFields();

        // If no field changes and no staged image, then nothing to do
        if (Object.keys(changedFields).length === 1 && !stagedImageKey) {
            dispatch(successAction('No changes detected to update.'));
            setEditing(false);
            return;
        }

        const transformedProduct: Partial<Product> & { imageStageKey?: string } = { ...changedFields };

        if ('unit' in changedFields) {
            transformedProduct.unit = product!.unit?.split(' - ')?.[0]?.trim() || product!.unit;
        }

        if ('quantityOption' in changedFields && typeof product!.quantityOption === 'string') {
            transformedProduct.quantityOption = product!.quantityOption.toLowerCase();
        }

        if (product!.groups !== undefined) {
            transformedProduct.groups = product!.groups || [];
        }

        if (product!.translationEn !== undefined) {
            transformedProduct.translationEn = product!.translationEn || '';
        }

        // If there is a staged image to include, attach the key
        if (stagedImageKey) {
            transformedProduct.imageStageKey = stagedImageKey;
        }

        createProductUpdateRequest(transformedProduct)
            .then(() => {
                setEditing(false);
                setProduct((prev) => (originalProduct ? { ...originalProduct } : prev));
                dispatch(
                    successAction(
                        'Product update request has been parked successfully. It needs to be authorized by another manager.'
                    )
                );
                // If image was part of this request, mark pending and clear staged key
                if (stagedImageKey) {
                    setHasPendingImageUpdate(true);
                    setProductImage(null);
                    setStagedImageKey(null);
                    if (stagedImagePreviewUrl) {
                        URL.revokeObjectURL(stagedImagePreviewUrl);
                        setStagedImagePreviewUrl(null);
                    }
                }
                // Refresh pending actions so red dot counters update immediately
                appDispatch(
                    AuthActionCreators.getPendingActions() as unknown as ThunkAction<
                        Promise<void>,
                        Store<RootAppState>,
                        void,
                        AnyAction
                    >
                );
            })
            .catch((err) => {
                if (err && err.status === 409) {
                    dispatch(
                        errorAction(
                            'There is already a pending update request for this product. Please authorize or reject it before parking a new one.'
                        )
                    );
                } else {
                    dispatch(errorAction('Cannot create product update request'));
                }
            });
    };

    const handleChange = useCallback(
        (event: { target: { name: string; value: string } } | ChangeEvent<HTMLInputElement>) => {
            const isTargetedEvent = (e: unknown): e is { target: { name: string; value: string } } => {
                if (!e || typeof e !== 'object') return false;
                const obj = e as { target?: { name?: unknown; value?: unknown } };
                const target = obj.target as { name?: unknown; value?: unknown } | undefined;
                return !!target && typeof target.name === 'string' && typeof target.value !== 'undefined';
            };

            if (isTargetedEvent(event)) {
                const { name, value } = event.target;
                setProduct((state) => {
                    if (!state) return state;
                    const fixedLabel = wfpFormat('fixed');
                    if (name === 'quantityOption' && state.maxAllowedQuantity === 1) {
                        return { ...state, quantityOption: fixedLabel } as Product;
                    }
                    return { ...state, [name]: value } as Product;
                });
            }
        },
        []
    );

    const handleMultiSelectChange = useCallback(
        (selectedOptions: { target?: Record<string, unknown> } | Array<{ value: string; label: string }>) => {
            let selectedIds: number[] = [];

            const isEventLike = (v: unknown): v is { target: Record<string, unknown> } =>
                !!v && typeof v === 'object' && 'target' in (v as Record<string, unknown>);

            if (isEventLike(selectedOptions) && selectedOptions.target) {
                const target = selectedOptions.target as Record<string, { value: number }>;

                const selectedOptionObjects = Object.keys(target)
                    .filter((key) => !isNaN(Number(key)))
                    .map((key) => target[key] as { value: number });

                selectedIds = selectedOptionObjects.map((option) => option.value);
            } else if (Array.isArray(selectedOptions)) {
                selectedIds = selectedOptions.map((o) => Number(o.value));
            }

            setProduct((state) => (state ? { ...state, groups: selectedIds } : state));
        },
        []
    );

    const handleMaxAllowedQuantityToggle = useCallback((checked: boolean) => {
        setProduct((state) => {
            if (!state) return state;
            const fixedLabel = wfpFormat('fixed');
            const next: Product = { ...state, maxAllowedQuantity: checked ? 1 : 0 };
            if (checked) {
                next.quantityOption = fixedLabel as string;
            }
            return next;
        });
    }, []);

    // UI groups selection removed

    const toggleEdit = useCallback(() => {
        setEditing((state) => {
            const next = !state;
            if (state && !next) {
                // cleaning up staged image data when leaving edit mode
                setStagedImageKey(null);
                // Revert form data to original product when exiting edit mode
                setProduct((prev) => (originalProduct ? { ...originalProduct } : prev));
                if (stagedImagePreviewUrl) {
                    URL.revokeObjectURL(stagedImagePreviewUrl);
                    setStagedImagePreviewUrl(null);
                }
            }
            return next;
        });
    }, [stagedImagePreviewUrl, originalProduct]);

    const markImageUpdatePending = useCallback(() => {
        setHasPendingImageUpdate(true);
        setProductImage(null);
    }, []);

    const refreshProduct = useCallback(() => {
        if (!productId) return;
        loadProduct(productId).then((loadedProduct) => {
            setProduct(loadedProduct);
            setOriginalProduct(loadedProduct);
        });
        loadProductImage(productId)
            .then(setProductImage)
            .catch(() => setProductImage(null));
    }, [productId]);

    return {
        editing,
        product,
        productImage,
        productId,
        toggleEdit,
        handleSubmit,
        handleChange,
        handleMultiSelectChange,
        handleMaxAllowedQuantityToggle,
        refreshProduct,
        categories,
        units,
        quantityOptions,
        limitationGroups,
        tz,
        hasPendingImageUpdate,
        markImageUpdatePending,
        stagedImageKey,
        setStagedImageKey,
        stagedImagePreviewUrl,
        setStagedImagePreviewUrl,
    };
}
